import { forwardRef, type ReactNode, type Ref } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Divider.css';

export interface DividerProps extends BaseProps {
  orientation?: 'horizontal' | 'vertical';
  label?: ReactNode;
  labelPosition?: 'left' | 'center' | 'right';
  thickness?: number;
}

/** Horizontal or vertical separator, with optional label. */
export const Divider = forwardRef(function Divider(
  { orientation = 'horizontal', label, labelPosition = 'center', thickness = 1, ...rest }: DividerProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const base = useBaseAttrs('rr-divider', rest, [
    `rr-divider--${orientation}`,
    !!label && `rr-divider--with-label`,
    !!label && `rr-divider--label-${labelPosition}`
  ]);
  const { dom } = splitBaseProps(rest);

  if (orientation === 'vertical') {
    return <div {...dom} ref={ref} role="separator" aria-orientation="vertical" {...base.attrs} className={base.className} style={{ ...base.style, width: thickness }} />;
  }

  if (label) {
    return (
      <div {...dom} ref={ref} role="separator" aria-orientation="horizontal" {...base.attrs} className={base.className} style={{ ...base.style, ['--rr-divider-thickness' as string]: `${thickness}px` }}>
        <span className="rr-divider__label">{label}</span>
      </div>
    );
  }

  return <div {...dom} ref={ref} role="separator" aria-orientation="horizontal" {...base.attrs} className={base.className} style={{ ...base.style, height: thickness }} />;
});
